import { sanitizeUrl } from './sanitizeUrl';

/**
 * Favicon URI template, matching Ditto's default service. Supports the RFC 6570
 * variables Ditto's templates use — `{hostname}`, `{origin}`, `{href}`, etc.
 */
export const FAVICON_URL_TEMPLATE = 'https://ditto.pub/api/favicon/{hostname}';

/**
 * Minimal RFC 6570 expansion covering the subset these templates need: simple
 * expansion (`{var}`, percent-encoded) and reserved expansion (`{+var}`).
 * Unknown variables expand to an empty string.
 */
export function fillUriTemplate(
  template: string,
  vars: Record<string, string | undefined>,
): string {
  return template.replace(/\{(\+?)([A-Za-z0-9_]+)\}/g, (_match, plus: string, name: string) => {
    const value = vars[name];
    if (value === undefined) return '';
    return plus ? encodeURI(value) : encodeURIComponent(value);
  });
}

/**
 * Build the favicon URL for a link. Returns `undefined` when the input isn't a
 * usable https URL, so callers can fall back to a generic icon.
 *
 * Like Ditto, `ai.` / `api.` / `enclave.` subdomains are stripped first — those
 * hosts rarely serve a favicon of their own, but their parent domain does.
 */
export function faviconUrl(
  url: string | undefined,
  template: string = FAVICON_URL_TEMPLATE,
): string | undefined {
  const safe = sanitizeUrl(url);
  if (!safe) return undefined;

  try {
    const parsed = new URL(safe);
    parsed.hostname = parsed.hostname.replace(/^(ai\.|api\.|enclave\.)/, '');
    return fillUriTemplate(template, {
      url: parsed.href,
      href: parsed.href,
      origin: parsed.origin,
      protocol: parsed.protocol,
      host: parsed.host,
      hostname: parsed.hostname,
      port: parsed.port,
      pathname: parsed.pathname,
      hash: parsed.hash,
      search: parsed.search,
    });
  } catch {
    return undefined;
  }
}
